import { useCallback, useEffect, useRef, useState } from 'react'

import { isTypingTarget } from '../lib/keys'
import type { PreviewItem } from './types'

const ZOOM_STEP = 0.5
const MIN_ZOOM = 1
const MAX_ZOOM = 6

/**
 * Resim goruntuleyici: sigdirilmis + zoom + pan.
 *
 * ## Zoom = 1 iken PAN YOK
 *
 * Resim viewport'a sigdirilmis durumda kaydirmanin anlami olmuyor ve
 * `grab` imleci gostermek kullaniciya calismayan bir sey vaat ederdi.
 *
 * ## `transform` kullaniliyor, `width`/`top` DEGIL
 *
 * Zoom ve pan compositor uzerinde kaliyor. `width` animasyonu her frame'de
 * layout tetikleyip buyuk resimlerde gorunur jank uretirdi.
 *
 * ## Klavye: `+` / `-` / `0`
 *
 * Fare tekerlegi tek basina yetmiyor - dokunmatik olmayan bir dizustunde
 * trackpad zoom'u tarayici zoom'u ile karisiyor. Acik tuslar ve butonlar
 * ikisi de var.
 */
export function ImageView({ item }: { item: PreviewItem }) {
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragging = useRef<{ x: number; y: number } | null>(null)

  // Yeni bir resme gecildiginde zoom SIFIRLANIYOR: onceki resimde 4x
  // zoom'ken sonrakine gecmek, kullaniciyi resmin ortasinda kaybederdi.
  useEffect(() => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [item.previewUrl])

  const adjust = useCallback((delta: number) => {
    setZoom((current) => {
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current + delta))
      // Tam sigdirmaya donunce kaydirma da sifirlaniyor, yoksa resim
      // ekranin disinda kaliyor.
      if (next === MIN_ZOOM) setOffset({ x: 0, y: 0 })
      return next
    })
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return
      if (event.key === '+' || event.key === '=') adjust(ZOOM_STEP)
      else if (event.key === '-') adjust(-ZOOM_STEP)
      else if (event.key === '0') {
        setZoom(1)
        setOffset({ x: 0, y: 0 })
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [adjust])

  const zoomed = zoom > MIN_ZOOM

  return (
    <div className="image-view">
      <div
        className={zoomed ? 'image-stage image-stage-pan' : 'image-stage'}
        onPointerDown={(event) => {
          if (!zoomed) return
          dragging.current = { x: event.clientX - offset.x, y: event.clientY - offset.y }
          // Pointer yakalama: fare resmin disina cikinca da surukleme
          // devam etmeli.
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onPointerMove={(event) => {
          const origin = dragging.current
          if (!origin) return
          setOffset({ x: event.clientX - origin.x, y: event.clientY - origin.y })
        }}
        onPointerUp={() => {
          dragging.current = null
        }}
        onDoubleClick={() => adjust(zoomed ? -(zoom - MIN_ZOOM) : ZOOM_STEP * 2)}
      >
        <img
          src={item.previewUrl}
          alt={item.filename}
          // Gercek boyutlar: layout shift'i onluyor. Inline-base64
          // surumunde bu bilgi YOKTU ve naif bir port onu KAYBEDERDI.
          width={item.width ?? undefined}
          height={item.height ?? undefined}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          }}
          draggable={false}
        />
      </div>

      <div className="image-tools">
        <button type="button" className="copy" onClick={() => adjust(-ZOOM_STEP)} disabled={!zoomed}>
          −
        </button>
        <span aria-live="polite">%{Math.round(zoom * 100)}</span>
        <button
          type="button"
          className="copy"
          onClick={() => adjust(ZOOM_STEP)}
          disabled={zoom >= MAX_ZOOM}
        >
          +
        </button>
        {item.width && item.height && (
          <span className="image-dims">
            {item.width}×{item.height}
          </span>
        )}
      </div>
    </div>
  )
}
